import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';

import Grid from '@material-ui/core/Grid';
import Collapse from '@material-ui/core/Collapse';
import Link from '@material-ui/core/Link';
import DropDownIcon from '@material-ui/icons/ArrowDropDownCircle';
import Typography from '@material-ui/core/Typography';
import FormHelperText from '@material-ui/core/FormHelperText';

import compose from 'helpers/compose';
import FormNumberField from 'components/FormFields/FormNumberField';
import useStyles from 'pages/Accounts/Edit/components/styles/moduleStyles';
import TextSwitch from 'components/FormFields/TextSwitch';

import {
  ExtProductAsset,
  ExtProductRegion,
  ExternalProductData,
  ExternalProductOptions,
  ExtProductType,
} from './model';
import { Currency } from 'enums';
import { ExternalProductsStore } from 'stores/workflowStores/finances/externalProducts/externalProducts';
import { valueFilledIn } from './ExternalProductModalSetup';

interface Props {
  productId?: string;
  options: ExternalProductOptions;
  formikProps: FormikProps<ExternalProductData>;
  currency: Currency;
  //setDefaultComposition: (formikProps: FormikProps<ExternalProductData>) => void;
  store: ExternalProductsStore;
  nominal: boolean;
  setNominal: (nominal: boolean) => void;
}

const ExternalProductModalComposition: React.FC<Props> = ({
  productId,
  options,
  formikProps,
  currency,
  store,
  nominal,
  setNominal
  //setDefaultComposition,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { values } = formikProps;
  const [showComposition, setShowComposition] = useState(!productId);
  const [compositionError, setCompositionError] = useState({
    asset: false,
    currency: false,
    region: false,
  });

  /*
  useEffect(() => {
    (async () => {
      formikProps.values.appliedValues !== nominal && formikProps.setFieldValue('applieValues', nominal); // gets reinitialized after submit with default value otherwise
    })();
  }, [nominal, formikProps]);
  */

  /*
  const resetValues = async (formikProps: FormikProps<ExternalProductData>, percentage: boolean) => {
    options.asset[values.type].forEach((k) => formikProps.setFieldValue(`asset.${k}`, 0));
    options.currency.forEach((k) => formikProps.setFieldValue(`currency.${k}`, 0));
    options.region.forEach((k) => formikProps.setFieldValue(`region.${k}`, 0));
    if (percentage) setDefaultComposition(formikProps);
    recalculateUnknown(formikProps, 'asset', true);
    recalculateUnknown(formikProps, 'currency', true);
    recalculateUnknown(formikProps, 'region', true);
  };
  */

  const recalculateUnknown = (
    formikProps: FormikProps<ExternalProductData>,
    field: 'asset' | 'currency' | 'region',
    //reset?: boolean,
  ) => {
    const { useValues } = formikProps.values;
    if (formikProps.values[field].UNKNOWN !== undefined) {
      const sum = Object.entries(formikProps.values[field])
        .filter((kv: [string, number]) => kv[0] !== 'UNKNOWN')
        .reduce((prev: number, f: [string, number]) => prev + Number(f[1]), 0);
      const max = useValues ? formikProps.values.balance.value : 100.1;
      if (sum <= max) {
        formikProps.setFieldValue(`${field}.UNKNOWN`, Math.max(0, (useValues ? max : 100) - sum));
        setCompositionError({ ...compositionError, [field]: false });
      } else {
        formikProps.setFieldValue(`${field}.UNKNOWN`, 0);
        setCompositionError({ ...compositionError, [field]: true });
      }
    }
  };

  const handlePercentageSwitch = async (formikProps: FormikProps<ExternalProductData>) => {
    await formikProps.setFieldValue('appliedValues', !nominal);
    await formikProps.setFieldValue('useValues', !nominal);
    await store.model({ ...formikProps.values, appliedValues: nominal, useValues: !nominal });
    setNominal(!nominal);
  };

  const lockedCompositions = [
    ExtProductType.ACCOUNT,
    ExtProductType.REAL_ESTATE,
    ExtProductType.REAL_ESTATE_RENT,
    ExtProductType.VALUABLES,
  ];

  const unlockedNominals: { [key in ExtProductAsset]: ExtProductType[] } = {
    [ExtProductAsset.CASH]: [ExtProductType.ACCOUNT],
    [ExtProductAsset.REAL_ESTATE]: [ExtProductType.REAL_ESTATE, ExtProductType.REAL_ESTATE_RENT],
    [ExtProductAsset.COMMODITY]: [ExtProductType.VALUABLES],
    [ExtProductAsset.STOCK]: [],
    [ExtProductAsset.BOND]: [],
    [ExtProductAsset.ALTERNATIVE]: [],
    [ExtProductAsset.CRYPTO]: [],
    [ExtProductAsset.UNKNOWN]: [],
    [ExtProductAsset.NONE]: [],
  };

  if (!values.type) return null;

  return (
    <>
      <Link
        className={classes.detailLink}
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          setShowComposition(!showComposition);
        }}
      >
        <span className={classes.detailControl}>
          {t('externalProduct_composition')}
          <DropDownIcon className={`${showComposition ? classes.shownIcon : ''}`} />
        </span>
      </Link>
      <Collapse in={showComposition}>
        <>
          <Grid container item xs={12}>
            <Grid item xs={12} sm={4} md={3}>
              <TextSwitch
                id="appliedValues"
                checked={nominal}
                afterChange={async () => handlePercentageSwitch(formikProps)}
                options={[
                  { label: t('externalProduct_composition_percentage') },
                  { label: t(`externalProduct_composition_${currency}`) },
                ]}
                disabled={!!values.accountId || (!valueFilledIn(values.balance.value) && !values.appliedValues)}
              />
            </Grid>
          </Grid>

          <Grid container item xs={12}>
            <Grid item>
              <Typography variant="h5">{t('externalProduct_composition_assets')}</Typography>
            </Grid>
          </Grid>
          <Grid container item xs={12} className={`${classes.compositionWrapper} ${nominal ? 'nominal' : ''}`}>
            {options.asset[values.type]
              //.filter((asset) => !nominal || asset !== ExtProductAsset.UNKNOWN)
              .map((asset) => (
                <Grid item key={asset}>
                  <FormNumberField
                    key={`asset.${asset}`}
                    name={`asset.${asset}`}
                    label={t(`externalProduct_asset_${asset}`)}
                    endAdornment={nominal ? t(`currency_${currency}`) : '%'}
                    disabled={
                      !!values.accountId ||
                      (!nominal && (asset === ExtProductAsset.UNKNOWN || lockedCompositions.includes(values.type))) ||
                      (nominal &&
                        lockedCompositions.includes(values.type) &&
                        !unlockedNominals[asset].includes(values.type))
                    }
                    afterChange={
                      asset === ExtProductAsset.UNKNOWN ? undefined : () => recalculateUnknown(formikProps, 'asset')
                    }
                    fast={false}
                  />
                </Grid>
              ))}
          </Grid>
          <Grid item xs={12}>
            <FormHelperText className={classes.formError}>
              {compositionError.asset && t('externalProduct_composition_error')}{' '}
            </FormHelperText>
          </Grid>

          <Grid container item xs={12}>
            <Grid item>
              <Typography variant="h5">{t('externalProduct_composition_currencies')}</Typography>
            </Grid>
          </Grid>
          <Grid container item xs={12} className={`${classes.compositionWrapper} ${nominal ? 'nominal' : ''}`}>
            {options.currency.map((c) => (
              <Grid item key={c}>
                <FormNumberField
                  key={`currency.${c}`}
                  name={`currency.${c}`}
                  label={[Currency.UNKNOWN, Currency.OTHER].includes(c) ? t(`currency_${c}`) : c}
                  endAdornment={nominal ? t(`currency_${currency}`) : '%'}
                  disabled={!nominal && (!!values.accountId || c === Currency.UNKNOWN)}
                  afterChange={c === Currency.UNKNOWN ? undefined : () => recalculateUnknown(formikProps, 'currency')}
                  fast={false}
                />
              </Grid>
            ))}
          </Grid>
          <Grid item xs={12}>
            <FormHelperText className={classes.formError}>
              {compositionError.currency && t('externalProduct_composition_error')}{' '}
            </FormHelperText>
          </Grid>

          <Grid container item xs={12}>
            <Grid item>
              <Typography variant="h5">{t('externalProduct_composition_regions')}</Typography>
            </Grid>
          </Grid>
          <Grid container item xs={12} className={`${classes.compositionWrapper} ${nominal ? 'nominal' : ''}`}>
            {options.region.map((region) => (
              <Grid item key={region}>
                <FormNumberField
                  key={`region.${region}`}
                  name={`region.${region}`}
                  label={t(`externalProduct_region_${region}`)}
                  endAdornment={nominal ? t(`currency_${currency}`) : '%'}
                  disabled={!nominal && (!!values.accountId || region === ExtProductRegion.UNKNOWN)}
                  afterChange={
                    region === ExtProductRegion.UNKNOWN ? undefined : () => recalculateUnknown(formikProps, 'region')
                  }
                  fast={false}
                />
              </Grid>
            ))}
          </Grid>
          <Grid item xs={12}>
            <FormHelperText className={classes.formError}>
              {compositionError.region && t('externalProduct_composition_error')}{' '}
            </FormHelperText>
          </Grid>
        </>
      </Collapse>
    </>
  );
};

export default compose(observer)(ExternalProductModalComposition);
